import React, { useState } from 'react';
import type { Zone } from '../../types';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';

const PRESET_COLORS = [
  '#4ade80','#86efac','#a3e635','#fb923c','#f472b6',
  '#60a5fa','#c084fc','#fbbf24','#34d399','#f87171',
];

interface ZoneFormProps {
  initial?: Partial<Zone>;
  onSubmit: (data: Partial<Zone>) => Promise<void>;
  onCancel: () => void;
  isEdit?: boolean;
}

export function ZoneForm({ initial, onSubmit, onCancel, isEdit }: ZoneFormProps) {
  const [form, setForm] = useState({ name: '', description: '', color: '#4ade80', ...initial });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required'); return; }
    setSaving(true);
    try {
      await onSubmit({ ...form, description: form.description || null });
    } catch (err) {
      setError((err as Error).message);
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {error && <p role="alert" className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/40 rounded-lg px-3 py-2">{error}</p>}
      <Input
        label="Zone Name *"
        value={form.name}
        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
        placeholder="e.g. Back Yard"
        required
      />
      <Textarea
        label="Description"
        value={form.description ?? ''}
        onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        placeholder="Notes about this zone…"
        rows={2}
      />
      <div>
        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Color</p>
        <div className="flex gap-2 flex-wrap">
          {PRESET_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setForm((f) => ({ ...f, color: c }))}
              className="w-7 h-7 rounded-full border-2 transition-transform hover:scale-110"
              style={{
                background: c,
                borderColor: form.color === c ? '#111' : 'white',
                outline: form.color === c ? `2px solid ${c}` : 'none',
              }}
            />
          ))}
          <input
            type="color"
            value={form.color}
            onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
            className="w-7 h-7 rounded cursor-pointer border border-gray-200"
            title="Custom color"
          />
        </div>
      </div>
      <div className="flex gap-2 pt-1">
        <Button type="submit" loading={saving} className="flex-1">{isEdit ? 'Save' : 'Add Zone'}</Button>
        <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
      </div>
    </form>
  );
}
